/**
 * Platform Context
 *
 * Provides dependency injection of platform-specific functionality via React Context.
 * Each app (Electron, Capacitor) wraps its root component in PlatformProvider
 * with its own implementation of PlatformAdapter.
 */

import { createContext, useContext, type ReactNode } from 'react'
import type { PlatformAdapter } from './types'

const PlatformContext = createContext<PlatformAdapter | null>(null)

export interface PlatformProviderProps {
  adapter: PlatformAdapter
  children: ReactNode
}

/**
 * Provides platform-specific functionality to child components.
 *
 * @example
 * // In Electron app
 * <PlatformProvider adapter={electronAdapter}>
 *   <SelfServicePage />
 * </PlatformProvider>
 *
 * @example
 * // In Capacitor app
 * <PlatformProvider adapter={capacitorAdapter}>
 *   <SelfServicePage />
 * </PlatformProvider>
 */
export function PlatformProvider({ adapter, children }: PlatformProviderProps) {
  return <PlatformContext.Provider value={adapter}>{children}</PlatformContext.Provider>
}

/**
 * Access the platform adapter from any child component.
 *
 * @throws Error if used outside of PlatformProvider
 *
 * @example
 * function MyComponent() {
 *   const platform = usePlatform()
 *   const settings = await platform.getStylingSettings()
 * }
 */
export function usePlatform(): PlatformAdapter {
  const adapter = useContext(PlatformContext)
  if (!adapter) {
    throw new Error('usePlatform must be used within a PlatformProvider')
  }
  return adapter
}

/**
 * Safely access optional platform features.
 * Returns undefined if the feature is not supported on the current platform.
 *
 * @example
 * const setFullscreen = usePlatformFeature('setFullscreen')
 * if (setFullscreen) {
 *   await setFullscreen(true)
 * }
 */
export function usePlatformFeature<K extends keyof PlatformAdapter>(
  feature: K
): PlatformAdapter[K] | undefined {
  const adapter = usePlatform()
  return adapter[feature]
}
